import React, { forwardRef } from 'react';
import { Link } from "react-router-dom";
import { Icon } from '..';
import type { IconType } from '../Icon';

interface ButtonProps {
    title: string
    color?: 'none' | 'navigate' | 'navigateBack' | 'default' | 'primary' | 'secondary' | 'warning' | 'danger' | 'success'
    textColor?: string
    icon?: IconType
    iconColor?: string
    iconSize?:number
    to?: string
    type?: 'button' | 'submit' | 'reset'
    onClick?: () => void
    className?: string
    small?: boolean
    loading?: boolean
    disabled?: boolean
    target?: string
}

// working colors : gray red yellow green cyan cyan indigo purple pink
const colorMap = {
    'none': 'transparent',
    'success' : 'green',
    'navigate': 'purple',
    'navigateBack': 'cyan',
    'default': 'gray',
    'primary': 'indigo',
    'secondary': 'cyan',
    'warning': 'yellow',
    'danger': 'red',
};

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(function Button(props, ref) {
    const {
        title, color, textColor, icon, iconColor, iconSize, to, type,
        onClick, className, small, loading, disabled, target
    } = props;

    const isDisabled = Boolean(disabled || loading);
    const isIconOnly = !title;
    // Transparent (`none`) buttons sit on page surfaces — use dark text, not white.
    const resolvedTextColor = textColor || (color === 'none' ? 'gray-900' : 'white');
    const noneTextDark = color === 'none' && !textColor ? 'dark:text-gray-100' : '';

    // Icon-only links (<a>) and <button> must share the same box: anchors inherit
    // line-height and can grow taller than buttons with the same padding.
    const classNames = [
        isIconOnly
            ? (small ? 'h-6 w-6 p-0' : 'h-9 w-9 p-0')
            : (small ? 'px-1.5 py-1' : 'px-3 py-2'),
        small ? 'text-xs' : 'text-sm',
        small ? 'rounded' : 'rounded-md',
        color === 'none' ? '' : 'shadow-sm',
        'inline-flex', 'items-center', 'justify-center', 'leading-none',
        `text-${resolvedTextColor}`, noneTextDark, 'font-semibold',
        'focus-visible:outline', 'focus-visible:outline-2',
        'focus-visible:outline-offset-2',
        `focus-visible:outline-${colorMap[color || 'default']}-600`,
        `bg-${colorMap[color || 'default']}-600`,
        isDisabled ? 'opacity-50 cursor-not-allowed' : `hover:bg-${colorMap[color || 'default']}-500`,
        className,
    ].join(' ');

    // Colored buttons use white text — icons should inherit that.
    // Transparent (`none`) buttons sit on page surfaces — use theme-aware gray.
    const resolvedIconColor = iconColor || (color === 'none' ? 'gray-900' : 'current');

    if (to) {
        return (
            <Link ref={ref as React.Ref<HTMLAnchorElement>} target={target} to={to} className={classNames} >
                {icon && (
                    <Icon type={loading ? 'loading' : icon} color={resolvedIconColor} size={iconSize || 5} className={title ? 'mr-1.5' : ''}/>
                )}
                { title }
            </Link>
        );
    }

    const _onClick = (e: AnyObj) => {
        e.stopPropagation();
        if (isDisabled || !onClick)
            return;
        onClick();
    };

    return (
        <button ref={ref as React.Ref<HTMLButtonElement>} type={type || 'button'} className={classNames} onClick={_onClick} disabled={isDisabled}>
            {icon && (
                <Icon type={loading ? 'loading' : icon} color={resolvedIconColor} size={iconSize || 5} className={title ? 'mr-1.5' : ''}/>
            )}
            { title }
        </button>
    );
});

export default Button;
